import React, { useCallback, useMemo, useRef } from 'react';
import { useEffect } from 'react';
import { useState } from 'react';
import { Filter } from '../../../models/filter/filter';
import { Api } from '../../../services/api';
import { Notify } from '../../../services/notification/notify';
import { SearchInput } from '../inputs/search';
import { useHasPermission } from '../../../services/auth/auth';
import { ActionButtonModes } from '../actions/actionButtons';
import { PageController } from '../grid/queryGrid/pageController';
import { useSearchParams } from 'react-router-dom';
import styles from './query.module.css';
import { formatNumber } from '../../../services/formatting';
import { SearchFilter } from '../filter/filter';

/**
 * Query component (cleaned & hardened)
 */
export const Query = ({
    singleResourceName,
    pluralResourceName,
    restEndpoint,
    listUrl,
    nameFunction = (raw) => raw.name,
    enableSearchBar = true,
    queryOnLoad = true,
    scope = null,
    maxResults = 30,
    actions = [],
    readPermissions = [],
    writePermissions = [],
    onSelect = null,
    refreshToken = null,
    queryParamPrefix = null,
    filters = null,
    children,
}) => {
    const [searchParams, setSearchParams] = useSearchParams();

    // data + UI state
    const [rawData, setRawData] = useState([]);
    const [page, _setPage] = useState(
        queryParamPrefix != null
            ? Math.max(parseInt(searchParams.get(queryParamPrefix + 'p') ?? '1') - 1, 0)
            : 0
    );
    const [pageData, setPageData] = useState({});
    const [loading, setLoading] = useState(false);
    const [disablePaging, setDisablePaging] = useState(false);
    const [query, _setQuery] = useState(queryParamPrefix != null ? searchParams.get(queryParamPrefix + 'q') || null : null);
    const [sort, _setSort] = useState(
        queryParamPrefix != null && searchParams.has(queryParamPrefix + 'sf') && searchParams.has(queryParamPrefix + 'sd')
            ? { field: searchParams.get(queryParamPrefix + 'sf'), direction: searchParams.get(queryParamPrefix + 'sd') }
            : null
    );
    const [searchFilters, _setSearchFilters] = useState({});
    const [selected, setSelected] = useState([]);
    const [isEditing, setIsEditing] = useState(false);

    // refresh index (programmatic refresh)
    const [refreshIndex, setRefreshIndex] = useState(0);

    // refs for aborting and "current" values that fetchNow reads when no overrides given
    const abortRef = useRef(null);
    const activeControllerRef = useRef(null);

    const queryRef = useRef(query);
    const sortRef = useRef(sort);
    const pageRef = useRef(page);
    const searchFiltersRef = useRef(searchFilters);

    // mark when the component has mounted (not strictly needed here but kept for clarity)
    const mountedRef = useRef(false);

    // user interaction marker — when user interacts via UI (search explicit, page, sort, or filters)
    const userInteractedRef = useRef(false);

    const canRead = useHasPermission(readPermissions);
    const canWrite = useHasPermission(writePermissions);

    // keep refs in sync with state
    useEffect(() => { queryRef.current = query; }, [query]);
    useEffect(() => { sortRef.current = sort; }, [sort]);
    useEffect(() => { pageRef.current = page; }, [page]);
    useEffect(() => { searchFiltersRef.current = searchFilters; }, [searchFilters]);

    // mark mounted
    useEffect(() => { mountedRef.current = true; }, []);

    // when refreshToken prop changes, trigger refresh
    useEffect(() => {
        if (refreshToken != null) {
            setRefreshIndex(i => i + 1);
        }
    }, [refreshToken]);

    // notify on select changes
    useEffect(() => {
        onSelect?.(selected.map(id => rawData?.find(r => r.id === id) ?? { id }));
    }, [selected, rawData, onSelect]);

    // --- stable fetch function that reads from refs unless overrides passed ---
    const fetchNow = useCallback(async (overrides = {}) => {
        // cancel previous
        abortRef.current?.abort();

        const controller = new AbortController();
        abortRef.current = controller;
        activeControllerRef.current = controller;

        const q = overrides.hasOwnProperty('query') ? overrides.query : queryRef.current;
        const s = overrides.hasOwnProperty('sort') ? overrides.sort : sortRef.current;
        const p = overrides.hasOwnProperty('page') ? overrides.page : pageRef.current;
        const f = overrides.hasOwnProperty('filters') ? overrides.filters : searchFiltersRef.current;

        const filter = new Filter({
            query: q,
            sort: s,
            page: p,
            pageSize: maxResults,
            scope,
            filters: f
        });

        // start loading
        setLoading(true);

        try {
            const json = await Api.get(
                restEndpoint,
                { q: JSON.stringify(filter) },
                controller.signal
            );

            // ensure this is still the latest request
            if (activeControllerRef.current !== controller) return;

            setRawData(json.content);
            setPageData({
                totalPages: json.totalPages,
                totalCount: json.totalCount,
                pageIndex: json.pageIndex
            });
        } catch (e) {
            if (e?.name === 'AbortError') {
                // intentional cancellation — ignore
                return;
            }
            console.error(e);
            Notify.error(
                `Error searching for ${pluralResourceName}`,
                `An error occurred while trying to search for ${pluralResourceName}.`
            );
        } finally {
            if (activeControllerRef.current === controller) {
                setLoading(false);
            }
        }
    // intentionally keep deps minimal so identity is stable
    }, [restEndpoint, maxResults, scope, pluralResourceName]);

    // effect for automatic fetching:
    //  - runs on mount only if queryOnLoad === true
    //  - runs when refreshIndex changes (programmatic refresh)
    // it does NOT run when queryOnLoad === false unless user interacts or refreshIndex bumps
    useEffect(() => {
        // if refresh was requested (refreshIndex > 0), always fetch
        if (refreshIndex > 0) {
            fetchNow();
            return;
        }

        // otherwise only auto-fetch on load if queryOnLoad is true
        if (queryOnLoad) {
            fetchNow();
        }
        // if queryOnLoad === false -> do nothing (user must interact or call refreshData)
    }, [fetchNow, refreshIndex, queryOnLoad]);

    // --- wrapped setters that mark user interaction and fetch as needed ---
    const refreshData = useCallback(() => {
        setRefreshIndex(i => i + 1);
    }, []);

    const setQueryAndMaybeFetch = useCallback((nextQuery, { explicit = false } = {}) => {
        const normalized = nextQuery === '' ? null : nextQuery;
        _setQuery(normalized);
        queryRef.current = normalized;

        // reset page on query change
        _setPage(0);
        pageRef.current = 0;

        userInteractedRef.current = true;

        if (explicit) {
            // explicit search — fetch immediately for user
            fetchNow({ query: normalized, page: 0 });
        }
    }, [fetchNow]);

    const setPageAndFetch = useCallback((nextPage) => {
        _setPage(nextPage);
        pageRef.current = nextPage;
        userInteractedRef.current = true;

        // fetch using new page
        fetchNow({ page: nextPage });
    }, [fetchNow]);

    // enforce canonical sort representation (null means no sort)
    const canonicalizeSort = useCallback((s) => {
        if (!s || !s.field) return null;
        return { field: String(s.field), direction: s.direction === 'down' ? 'down' : 'up' };
    }, []);

    const setSortAndFetch = useCallback((nextSort) => {
        const canonical = canonicalizeSort(nextSort);
        _setSort(canonical);
        sortRef.current = canonical;
        userInteractedRef.current = true;

        // when sort changes, go back to page 0
        _setPage(0);
        pageRef.current = 0;

        fetchNow({ sort: canonical, page: 0 });
    }, [fetchNow, canonicalizeSort]);

    const setSearchFiltersAndFetch = useCallback((nextFilters) => {
        _setSearchFilters(nextFilters);
        searchFiltersRef.current = nextFilters;
        userInteractedRef.current = true;

        // reset to first page when filters change
        _setPage(0);
        pageRef.current = 0;

        fetchNow({ filters: nextFilters, page: 0 });
    }, [fetchNow]);

    // --- URL param syncing (only after initial mount; safe because auto-fetch is gated above) ---
    useEffect(() => {
        if (!mountedRef.current) return;

        if (queryParamPrefix != null) {
            setSearchParams(sp => {
                if (query == null) sp.delete(queryParamPrefix + 'q');
                else sp.set(queryParamPrefix + 'q', query);
                return sp;
            }, { replace: true });
        }
    }, [query, queryParamPrefix, setSearchParams]);

    useEffect(() => {
        if (!mountedRef.current) return;

        if (queryParamPrefix != null) {
            setSearchParams(sp => {
                if (page == 0) sp.delete(queryParamPrefix + 'p');
                else sp.set(queryParamPrefix + 'p', (page + 1).toString());
                return sp;
            }, { replace: true });
        }
    }, [page, queryParamPrefix, setSearchParams]);

    useEffect(() => {
        if (!mountedRef.current) return;

        if (queryParamPrefix != null) {
            setSearchParams(sp => {
                if (sort == null || sort.field == null) {
                    sp.delete(queryParamPrefix + 'sf');
                    sp.delete(queryParamPrefix + 'sd');
                } else {
                    sp.set(queryParamPrefix + 'sf', sort.field);
                    sp.set(queryParamPrefix + 'sd', sort.direction);
                }
                return sp;
            }, { replace: true });
        }
    }, [sort, queryParamPrefix, setSearchParams]);

    // helper names for children
    const selectedName = () => (selected.length === 0 ? singleResourceName : pluralResourceName);
    const selectedNames = () => selected.map(id => nameFunction(rawData?.find(r => r.id === id) ?? [])).join(', ');

    // render
    return (
        <section className={styles.Query}>
            <header className={styles.Header}>
                {enableSearchBar && (
                    <div className={styles.SearchWrapper}>
                        <SearchInput
                            placeholder={'Search ' + pluralResourceName}
                            disabled={loading}
                            onSearch={(q, _, explicit) => {
                                // same semantics you had before: only run immediate fetch when explicit
                                setQueryAndMaybeFetch(q, { explicit });
                            }}
                            defaultValue={query}
                        />
                    </div>
                )}

                {actions.map((Action, i) => (
                    <Action
                        key={Action.toString() + i.toString()}
                        name={selectedNames()}
                        ids={selected}
                        data={rawData}
                        scope={scope}
                        nameFunction={nameFunction}
                        resourceName={selectedName()}
                        restEndpoint={restEndpoint}
                        setSelected={setSelected}
                        refreshData={refreshData}
                        mode={ActionButtonModes.Query}
                        canRead={canRead}
                        canWrite={canWrite}
                        listUrl={listUrl}
                        isEditing={isEditing}
                        setIsEditing={setIsEditing}
                        setLoading={setLoading}
                        disablePaging={disablePaging}
                        setDisablePaging={setDisablePaging}
                    />
                ))}

                {filters && filters.length > 0 && (
                    <div className={styles.Filters}>
                        {(filters ?? []).map((f) => (
                            <SearchFilter
                                key={f.key}
                                filter={f}
                                searchFilters={searchFilters}
                                setSearchFilters={(nf) => setSearchFiltersAndFetch(nf)}
                                loading={loading}
                            />
                        ))}
                    </div>
                )}

                <div className={styles.SearchMessage}>
                    {loading ? 'Searching...' :
                        `Showing ${formatNumber(pageData.totalCount ?? 0)} result${pageData.totalCount !== 1 ? 's' : ''} ${query ? ` for "${query}"` : ''}`}
                </div>
            </header>

            {children({
                loading,
                rawData,
                page,
                setPage: setPageAndFetch,
                pageData,
                setPageData,
                sort,
                setSort: setSortAndFetch,
                query,
                setQuery: (q) => setQueryAndMaybeFetch(q, { explicit: true }), // expose setQuery that triggers a search
                getData: (overrides) => fetchNow(overrides), // allow children to call fetchNow directly if desired
                canWrite,
                canRead,
                selected,
                setSelected,
                isEditing,
                setIsEditing,
                refreshData,
                setLoading,
                disablePaging,
                setDisablePaging,
                setSearchFilters: setSearchFiltersAndFetch,
            })}

            <PageController
                pageIndex={pageData.pageIndex}
                totalPages={pageData.totalPages}
                pageState={[page, (p) => setPageAndFetch(p)]}
                disabled={loading || disablePaging}
            />
        </section>
    );
};
